import {
  ENTITY_TYPE_GROUPS,
  EntityTypeSelector,
  type TEntityChoice,
  type TEntityTypeGroupKey,
} from '@verifime/components';
import { useState } from 'react';
import { CHOOSE_ENTITY_INTRO } from './chooseEntityCopy';

/**
 * The workflow types the choose screen distinguishes. A local union rather than the generated
 * `WorkflowType`, so callers need not import `@verifime/api-definition` for it.
 */
export type TChooseEntityWorkflowType = 'DEFAULT' | 'ORGANISATION_ONLY';

/**
 * A choice carried in from elsewhere (the URL) counts as none when a declared group does not offer
 * it: its tile is not on screen, so the selector would otherwise leave Continue enabled for a choice
 * the customer cannot see.
 */
function choiceOffered(
  choice: TEntityChoice | null,
  declaredGroup: TEntityTypeGroupKey | undefined,
): TEntityChoice | null {
  if (choice === null || declaredGroup === undefined || choice === 'individual') {
    return choice;
  }
  const offered: readonly TEntityChoice[] = ENTITY_TYPE_GROUPS[declaredGroup].entityTypes;
  return offered.includes(choice) ? choice : null;
}

export type ChooseEntityViewProps = {
  /**
   * The client's onboarding workflow type. `DEFAULT` offers the Individual tile, with the intro,
   * and every entity; `ORGANISATION_ONLY` shows the Individual tile disabled and no intro, so an
   * entity is required. `INDIVIDUAL_ONLY` has nothing to choose: the app goes straight to the
   * confirm screen, and this view renders nothing if given it, leaving the caller to decide.
   */
  workflowType: TChooseEntityWorkflowType | 'INDIVIDUAL_ONLY';
  /**
   * The one group the invite declared (a business, a company or a trust). Leave it out for no
   * declaration. Only that group shows, and the Individual tile and intro appear as they do under
   * `ORGANISATION_ONLY`: the intro tells the customer to pick Individual, which a declared group
   * rules out. This only limits what the screen offers; person-api refuses a choice outside the
   * declaration.
   */
  declaredGroup?: TEntityTypeGroupKey;
  /**
   * The choice to show selected on arrival, for example when the customer comes back to change
   * it.
   */
  initialChoice: TEntityChoice | null;
  /** Called with the choice on Continue: an entity type, or `individual` for just me. */
  onChoose: (choice: TEntityChoice) => void;
  /**
   * Called when Cancel is clicked. Leave it out and the view shows no Cancel button, for a caller
   * that must not let the customer leave without choosing.
   */
  onCancel?: () => void;
};

/**
 * The choose screen: the customer picks what they are onboarding, on the shared
 * EntityTypeSelector. It saves nothing: Continue hands the choice to the caller, which shows the
 * confirm screen (where the choice is confirmed) or wherever it decides (ADR 62: navigation is the
 * caller's, via the callbacks).
 */
export default function ChooseEntityView({
  workflowType,
  declaredGroup,
  initialChoice,
  onChoose,
  onCancel,
}: Readonly<ChooseEntityViewProps>) {
  const [choice, setChoice] = useState<TEntityChoice | null>(initialChoice);

  if (workflowType === 'INDIVIDUAL_ONLY') {
    return null;
  }

  const requiresEntity = workflowType === 'ORGANISATION_ONLY' || declaredGroup !== undefined;
  // No declared group: leave the order to the selector's default.
  const groupOrder = declaredGroup !== undefined ? [declaredGroup] : undefined;
  const offeredChoice = choiceOffered(choice, declaredGroup);

  // The selector keeps Continue disabled until there is a choice it accepts (with the Individual
  // tile disabled, a stale `individual` counts as none), so onContinue always carries a valid one.
  return (
    <EntityTypeSelector
      dataTestId="choose-entity-view"
      individualTile={requiresEntity ? 'disabled' : 'enabled'}
      intro={requiresEntity ? undefined : CHOOSE_ENTITY_INTRO}
      groupOrder={groupOrder}
      selected={offeredChoice}
      onSelect={setChoice}
      onContinue={onChoose}
      onCancel={onCancel}
    />
  );
}
